import {
  EXPRESS_APPLICATION_ID_STORAGE_KEY,
  EXPRESS_JOINT_NEW_FLOW,
  writeStoredExpressApplicationId,
} from './express-application-progress'

export const CAO_APPLICATION_ID_STORAGE_KEY = 'cao_application_id'

export type ConsumerResumePath = '/apply' | '/apply/express'

export function isExpressApplicationRecord(input: {
  applicationId: string
  applicationType?: string | null
}): boolean {
  const applicationId = input.applicationId.trim()
  if (!applicationId) {
    return false
  }
  return input.applicationType === 'express' || applicationId.startsWith('express_')
}

export function isExpressJointNewApplication(input: {
  applicationId: string
  applicationType?: string | null
  expressFlow?: string | null
}): boolean {
  if (!isExpressApplicationRecord(input)) {
    return false
  }
  return input.expressFlow === EXPRESS_JOINT_NEW_FLOW
}

export function getConsumerApplicationResumePath(input: {
  applicationId: string
  applicationType?: string | null
}): ConsumerResumePath {
  return isExpressApplicationRecord(input) ? '/apply/express' : '/apply'
}

export function buildConsumerResumeUrl(input: {
  applicationId: string
  applicationType?: string | null
}): string {
  const path = getConsumerApplicationResumePath(input)
  return `${path}?resume=${encodeURIComponent(input.applicationId.trim())}`
}

export function readStoredCaoApplicationId(): string | null {
  if (typeof window === 'undefined') {
    return null
  }
  try {
    const value = window.sessionStorage.getItem(CAO_APPLICATION_ID_STORAGE_KEY)
    return typeof value === 'string' && value.trim() ? value.trim() : null
  } catch {
    return null
  }
}

export function writeStoredCaoApplicationId(applicationId: string | null): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    if (applicationId?.trim()) {
      window.sessionStorage.setItem(CAO_APPLICATION_ID_STORAGE_KEY, applicationId.trim())
    } else {
      window.sessionStorage.removeItem(CAO_APPLICATION_ID_STORAGE_KEY)
    }
  } catch {
    // Non-fatal.
  }
}

export function clearCaoApplicationResumeMarker(): void {
  writeStoredCaoApplicationId(null)
}

export function clearExpressApplicationResumeMarker(): void {
  writeStoredExpressApplicationId(null)
}

/** Clear both consumer resume markers so a fresh start cannot auto-resume the wrong flow. */
export function clearAllConsumerResumeMarkers(): void {
  clearCaoApplicationResumeMarker()
  clearExpressApplicationResumeMarker()
}

export function syncConsumerResumeMarker(input: {
  applicationId: string
  applicationType?: string | null
}): void {
  if (isExpressApplicationRecord(input)) {
    clearCaoApplicationResumeMarker()
    writeStoredExpressApplicationId(input.applicationId)
    return
  }
  clearExpressApplicationResumeMarker()
  writeStoredCaoApplicationId(input.applicationId)
}
